import { useCallback, useSyncExternalStore } from 'react';
import { type Query, type QueryKey, useQuery } from '@tanstack/react-query';

import { isRoleChangedPayload } from '@/auth/roleChange';
import { subscribeSession } from '@/auth/session';
import {
  type DataState,
  dataState,
  isEmptyData,
  statusOfAnswer,
} from '@/components/shared/AccessState/accessAnswer';
import { activeAssignmentId, activeKey, isAssignmentKey } from '@/query/keys';
import { MAX_RETRIES, queryClient } from '@/query/queryClient';
import type { HeaderSelection } from '@/selection/headerSelection';
import { useHeaderSelection } from '@/selection/useHeaderSelection';

/**
 * The one hook for the request of a chart or a list (UI state plan, part 8b).
 *
 * It wraps TanStack Query around ONE ApiProvider call and gives every page
 * the same five states (`loading`, `ok`, `no_data`, `no_access` / `not_found`,
 * `error`), read from the server answer by AccessState/accessAnswer.ts.
 *
 * - Key: `['a', <active assignment id>, endpoint, params]` (query/keys.ts), so
 *   an answer loaded under one role is never shown under another. `params`
 *   is everything that decides the answer: the page's filters, ids, paging
 *   and, where the request sends it, the header selection (the function form
 *   of `params` gets it). The hook follows the header selection and the
 *   active assignment: when one changes, the key changes and the request is
 *   sent again.
 * - Late answers: the request gets TanStack's `signal`. When the page is
 *   left, the role switched or a filter changed, the request is aborted and
 *   its answer is dropped; it can never overwrite the newer one.
 * - No retry of an answer the server gave: 401 / 403 / 404 / 409 and every
 *   other `success: false` body are answers. Only a request that got no
 *   answer at all (network failure, a 5xx) is sent once more.
 * - `staleTime` 0 (PAGE_DATA_STALE_TIME): every mount asks the server again,
 *   as the pages did before. Nothing reloads on window focus or reconnect.
 * - `gcTime` 0 (PAGE_DATA_GC_TIME): an answer is kept only while a page shows
 *   it. A page that is opened again starts with its loading state, as before,
 *   and never shows an answer from before a write of a page that does not
 *   invalidate yet. Raising it makes pages show the last answer at once while
 *   the new one loads (the plan's target, once the writes invalidate).
 *
 * ApiProvider stays the HTTP client: `request` calls it and resolves with its
 * answer (ApiProvider resolves refusals as `{ success: false, ... }` bodies).
 */

/** Page data is asked again on every mount (the pages' behaviour before part 8b). */
export const PAGE_DATA_STALE_TIME = 0;

/** An answer no page shows any more is dropped at once (no answer from before a write is shown). */
export const PAGE_DATA_GC_TIME = 0;

/** Statuses that are an answer, never retried. */
export const ANSWER_STATUSES = [401, 403, 404, 409];

/** `idle`: nothing is asked (the query is disabled); `loading`: no answer yet. */
export type AccessQueryState = 'idle' | 'loading' | DataState;

/** How a request failed: the state to show, and whether the server answered. */
export class AccessAnswerError extends Error {
  readonly state: Exclude<DataState, 'ok' | 'no_data'>;
  readonly status: number | null;
  /** false: no answer from the server (network failure or 5xx): the only case sent again. */
  readonly answered: boolean;

  constructor(
    state: Exclude<DataState, 'ok' | 'no_data'>,
    status: number | null,
    answered: boolean,
  ) {
    // never the server's message: a refusal does not describe the access structure
    super(state);
    this.name = 'AccessAnswerError';
    this.state = state;
    this.status = status;
    this.answered = answered;
  }
}

/** Once more for a request without an answer; never for an answer (401 / 403 / 404 / 409, ...). */
export const retryAccessQuery = (failureCount: number, error: unknown): boolean => {
  if (!(error instanceof AccessAnswerError)) return false;
  if (error.answered) return false;
  if (error.status !== null && ANSWER_STATUSES.includes(error.status)) return false;
  return failureCount < MAX_RETRIES;
};

/**
 * A request body as a key part. FormData and URLSearchParams have no JSON
 * form (every body would hash to `{}`), so they become their entries.
 */
export const keyPart = (value: unknown): unknown => {
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    return value.toString();
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return Array.from(value.entries()).map(([name, entry]) => [
      name,
      typeof entry === 'string' ? entry : `file:${entry.name}:${entry.size}`,
    ]);
  }
  return value ?? null;
};

/** The key of a request under the ACTIVE assignment (also for invalidating it after a write). */
export const accessQueryKey = (endpoint: string, params?: unknown): QueryKey =>
  params === undefined ? activeKey(endpoint) : activeKey(endpoint, keyPart(params));

export interface AccessRequestContext {
  /** Aborted when the answer is no longer wanted: pass it to ApiProvider. */
  signal: AbortSignal;
  /** The header selection the key was built with. */
  selection: HeaderSelection;
}

export interface AccessQueryOptions<TAnswer> {
  /** What is asked: the endpoint (or a name of the request), the first part of the key. */
  endpoint: string;
  /**
   * Everything that decides the answer (filters, ids, paging; a FormData /
   * URLSearchParams body is fine). The function form gets the header
   * selection, for requests that send it.
   */
  // eslint-disable-next-line no-unused-vars
  params?: unknown | ((selection: HeaderSelection) => unknown);
  /** The ApiProvider call. Resolves with the server answer (`{ success, data, ... }`). */
  // eslint-disable-next-line no-unused-vars
  request: (context: AccessRequestContext) => Promise<TAnswer>;
  /** false: nothing is asked (state `idle`). */
  enabled?: boolean;
  /** Decides "no data" on `answer.data` (default: null / empty list / empty object). */
  // eslint-disable-next-line no-unused-vars
  isEmpty?: (data: unknown) => boolean;
  /**
   * Keep showing the previous answer while the next page / filter loads
   * (never an answer of another role assignment).
   */
  keepPrevious?: boolean;
  /** Default PAGE_DATA_STALE_TIME (0). */
  staleTime?: number;
  /** Opt-in retention; existing pages still discard inactive results immediately. */
  gcTime?: number;
  /** Revalidate stale data when returning from another tab, for opted-in pages. */
  refetchOnWindowFocus?: boolean;
}

export interface AccessQueryResult<TAnswer> {
  state: AccessQueryState;
  /** The server answer while the state is `ok` or `no_data`, else undefined. */
  answer: TAnswer | undefined;
  /** `answer.data`. */
  data: TAnswer extends { data?: infer TData } ? TData | undefined : unknown;
  /** No answer yet and a request is running (the first load). */
  isLoading: boolean;
  /** A request is running, also behind an answer that is shown. */
  isFetching: boolean;
  /** The answer shown belongs to the previous params (`keepPrevious`). */
  isPrevious: boolean;
  /** Asks again (a late answer of the running request is dropped). */
  refetch: () => Promise<unknown>;
  queryKey: QueryKey;
}

/**
 * `keepPrevious`: the previous answer stays while the next one loads, but only
 * an answer of the SAME request under the SAME role assignment. After a role
 * switch nothing of the previous role is kept on screen.
 */
const previousOfSameRole =
  (endpoint: string) =>
  <TAnswer>(
    previousData: TAnswer | undefined,
    previousQuery: Query<TAnswer, AccessAnswerError> | undefined,
  ): TAnswer | undefined =>
    previousQuery &&
    isAssignmentKey(previousQuery.queryKey, activeAssignmentId()) &&
    previousQuery.queryKey[2] === endpoint
      ? previousData
      : undefined;

const aborted = (): Error => {
  const error = new Error('aborted');
  error.name = 'AbortError';
  return error;
};

/** The request got no answer from the server (the fetch failed, or a 5xx without a body the UI can read). */
const withoutAnswer = (answer: unknown, status: number | null): boolean => {
  if (answer instanceof Error) return true;
  if (answer === null || answer === undefined) return true;
  if (typeof answer === 'object' && (answer as { status?: unknown }).status === 0) return true;
  return status !== null && status >= 500;
};

export function useAccessQuery<TAnswer = any>({
  endpoint,
  params,
  request,
  enabled = true,
  isEmpty = isEmptyData,
  keepPrevious = false,
  staleTime = PAGE_DATA_STALE_TIME,
  gcTime = PAGE_DATA_GC_TIME,
  refetchOnWindowFocus = false,
}: AccessQueryOptions<TAnswer>): AccessQueryResult<TAnswer> {
  // re-renders when the active assignment or the header selection changes: the key follows both
  useSyncExternalStore(subscribeSession, activeAssignmentId, activeAssignmentId);
  const selection = useHeaderSelection();
  const resolvedParams = typeof params === 'function' ? params(selection) : params;
  const queryKey = accessQueryKey(endpoint, resolvedParams ?? null);

  const query = useQuery<TAnswer, AccessAnswerError>(
    {
      queryKey,
      queryFn: async ({ signal }) => {
        let answer: TAnswer;
        try {
          answer = await request({ signal, selection });
        } catch {
          if (signal.aborted) throw aborted();
          throw new AccessAnswerError('error', null, false);
        }
        // ApiProvider resolves an aborted request too: its answer is not one
        if (signal.aborted) throw aborted();
        const state = dataState(answer, isEmpty);
        if (state === 'ok' || state === 'no_data') return answer;
        const status = isRoleChangedPayload(answer) ? 409 : statusOfAnswer(answer);
        throw new AccessAnswerError(state, status, !withoutAnswer(answer, status));
      },
      enabled,
      staleTime,
      gcTime,
      retry: retryAccessQuery,
      // as the pages before: the request is always sent, and only when the page asks
      networkMode: 'always',
      refetchOnWindowFocus,
      refetchOnReconnect: false,
      // every answer is a new object, as it was for the pages' own state
      structuralSharing: false,
      placeholderData: keepPrevious ? previousOfSameRole(endpoint) : undefined,
    },
    queryClient,
  );

  const { data: answer, error, isError, isFetching, isPlaceholderData, refetch: run } = query;

  let state: AccessQueryState;
  // a failed request never leaves an older answer on the screen
  if (isError) state = error instanceof AccessAnswerError ? error.state : 'error';
  else if (answer !== undefined) state = dataState(answer, isEmpty);
  else state = enabled ? 'loading' : 'idle';

  const refetch = useCallback(() => run(), [run]);
  const shown = state === 'ok' || state === 'no_data' ? answer : undefined;

  return {
    state,
    answer: shown,
    data: (shown as { data?: unknown } | undefined)?.data as AccessQueryResult<TAnswer>['data'],
    isLoading: state === 'loading',
    isFetching,
    isPrevious: isPlaceholderData,
    refetch,
    queryKey,
  };
}

/**
 * After a successful write that answers with the new record: that record
 * becomes the answer of the read with this key (no second request).
 */
export const setAccessQueryData = (queryKey: QueryKey, data: unknown): void => {
  queryClient.setQueryData(queryKey, { success: true, data });
};

/**
 * After a successful write: the requests of these endpoints (under the active
 * assignment, whatever their params) are asked again where they are on
 * screen, and dropped elsewhere. Forms keep their own code; they only
 * call this once the server confirmed the write.
 */
export const invalidateAccessQueries = (...endpoints: string[]): Promise<void> =>
  Promise.all(
    endpoints.map((endpoint) => {
      const queryKey = activeKey(endpoint);
      // an answer from before the write is never shown again
      queryClient.removeQueries({ queryKey, type: 'inactive' });
      return queryClient.invalidateQueries({ queryKey });
    }),
  ).then(() => undefined);
